import { useCallback, useEffect, useRef, useState } from "react";
import { cn } from "../utils";
import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";

type Props = {
  children: React.ReactNode;
  className?: string;
  scrollAmount?: number;
};

export default function HorizontalList({
  children,
  className,
  scrollAmount = 80,
}: Props) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(
      Math.ceil(el.scrollLeft + el.clientWidth) < el.scrollWidth,
    );
  }, []);

  useEffect(() => {
    checkScroll();
    const el = scrollContainerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(checkScroll);
    observer.observe(el);
    return () => observer.disconnect();
  }, [checkScroll, children]);

  const scroll = (direction: "left" | "right") => {
    scrollContainerRef.current?.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };

  const isOverflowing = canScrollLeft || canScrollRight;

  return (
    <div className="relative flex w-full items-center">
      {isOverflowing && (
        <button
          className={cn(
            "absolute -left-6 z-10 flex h-full cursor-pointer items-center text-text-secondary",
            !canScrollLeft && "opacity-0",
          )}
          onClick={() => scroll("left")}
          disabled={!canScrollLeft}
        >
          <IconChevronLeft size={16} />
        </button>
      )}

      <div
        ref={scrollContainerRef}
        onScroll={checkScroll}
        className={cn(
          "flex w-full overflow-x-auto",
          "[-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
          className,
        )}
      >
        {children}
      </div>

      {isOverflowing && (
        <button
          className={cn(
            "absolute -right-6 z-10 flex h-full cursor-pointer items-center text-text-secondary",
            !canScrollRight && "opacity-0",
          )}
          onClick={() => scroll("right")}
          disabled={!canScrollRight}
        >
          <IconChevronRight size={16} />
        </button>
      )}
    </div>
  );
}
